import { LmgService } from "../resource";

// Pre-LIMIT row count for the policy picker. policies-available is capped at
// 2000 rows server-side and reports the true (pre-limit) total on the response
// envelope's `Datum` (StDmsAvailableMeta{Total}) so the FE can warn that the
// list was truncated. Read it defensively — accept either casing / a couple of
// plausible spellings — and when the field is absent (BE not deployed yet) fall
// back to the number of rows actually delivered. total === delivered means
// "nothing was truncated", i.e. exactly the pre-change behaviour.
function readAvailableTotal(body, rowCount) {
  const datum = body && body.Datum ? body.Datum : {};
  const keys = ["Total", "total", "TotalCount", "total_count"];
  for (const k of keys) {
    const raw = datum[k];
    if (raw === null || raw === undefined || raw === "") continue;
    if (typeof raw !== "number" && typeof raw !== "string") continue;
    const n = Number(raw);
    if (!isNaN(n) && n >= 0) return n;
  }
  return rowCount;
}

// "Already delivered" matches for an explicit policy search. The BE resolves the
// policies its NOT-IN filter hid to the batch holding them and returns them on
// `Datum.blocked` — NEVER inside `Data`, so a blocked policy is structurally
// unable to become a tickable row. Both keys are `omitempty`, i.e. absent when no
// `policy` param was sent or nothing matched, which keeps the default browse
// response byte-identical to what the FE consumed before. Absent => empty list,
// so deploy order between FE and BE doesn't matter.
function readBlocked(body) {
  const datum = body && body.Datum ? body.Datum : {};
  const raw = datum.blocked !== undefined ? datum.blocked : datum.Blocked;
  if (!Array.isArray(raw)) return [];
  return raw.filter((b) => b && typeof b === "object" && b.billing_id != null);
}

// Pre-limit count of the above (the array itself is capped at 50 server-side), so
// the FE can say "แสดง 50 จาก N". Falls back to the number of rows delivered.
function readBlockedTotal(body, rowCount) {
  const datum = body && body.Datum ? body.Datum : {};
  const raw =
    datum.blocked_total !== undefined ? datum.blocked_total : datum.BlockedTotal;
  if (typeof raw === "number" || typeof raw === "string") {
    const n = Number(raw);
    if (!isNaN(n) && n >= 0) return n;
  }
  return rowCount;
}

// DMS batch (Phase 3) — สร้าง/list ชุดนำส่ง DMS + ตัด ledger OUT (atomic, BE)
const DmsModule = {
  namespaced: true,
  state: {
    // กรมธรรม์ที่ยังเลือกได้ (ไม่อยู่ใน DMS batch อื่น) — สำหรับ policy picker
    available: [],
    // จำนวนกรมธรรม์ทั้งหมดที่ตรงเงื่อนไข (ก่อนตัดที่ 2000) — ใช้เตือนว่าโดนตัด
    availableTotal: 0,
    // กรมธรรม์ที่ค้นเจอแต่ "อยู่ในชุดนำส่ง DMS แล้ว" — ไม่ใช่แถวที่เลือกได้
    // (มาจาก Datum.blocked, สูงสุด 50 แถว) ใช้บอกเหตุผลแทนผลลัพธ์ว่าง
    blocked: [],
    blockedTotal: 0,
    // รายการชุดนำส่ง DMS
    batches: [],
    isLoading: false,
    isLoadingAvailable: false,
  },
  mutations: {
    SET_AVAILABLE(state, payload) {
      state.available = payload;
    },
    SET_AVAILABLE_TOTAL(state, payload) {
      state.availableTotal = payload;
    },
    SET_BLOCKED(state, payload) {
      state.blocked = payload;
    },
    SET_BLOCKED_TOTAL(state, payload) {
      state.blockedTotal = payload;
    },
    SET_BATCHES(state, payload) {
      state.batches = payload;
    },
  },
  actions: {
    // โหลดกรมธรรม์ที่ยังเลือกได้ (filter ตาม round ได้)
    async loadAvailable({ commit, state }, payload) {
      state.isLoadingAvailable = true;
      try {
        const res = await LmgService.DmsPoliciesAvailable(payload || {});
        const rows = res.data.Data == null ? [] : res.data.Data;
        commit("SET_AVAILABLE", rows);
        commit("SET_AVAILABLE_TOTAL", readAvailableTotal(res.data, rows.length));
        const blocked = readBlocked(res.data);
        commit("SET_BLOCKED", blocked);
        commit("SET_BLOCKED_TOTAL", readBlockedTotal(res.data, blocked.length));
      } catch (err) {
        commit("SET_AVAILABLE", []);
        commit("SET_AVAILABLE_TOTAL", 0);
        commit("SET_BLOCKED", []);
        commit("SET_BLOCKED_TOTAL", 0);
      } finally {
        state.isLoadingAvailable = false;
      }
    },
    // สร้างชุดนำส่ง (payload = FormData) → atomic ที่ BE
    createBatch(_, formData) {
      return LmgService.DmsBatchCreate(formData);
    },
    // แก้ไขชุดนำส่งเดิม (payload = { id, formData }) → atomic ที่ BE
    updateBatch(_, { id, formData }) {
      return LmgService.DmsBatchUpdate(id, formData);
    },
    // โหลด list ชุดนำส่ง
    //
    // payload = { batchNumber?, year?, month? } — ตัวกรองของหน้า list
    //   batchNumber : LIKE บางส่วนของเลขที่ใบนำส่ง
    //   year        : ทั้งปี ตาม batch_date (วันที่นำส่ง — ไม่ใช่ created_at และ
    //                 ไม่ใช่คอลัมน์ month/year ซึ่งค่าจริงไม่ตรงกัน)
    //   year+month  : เฉพาะเดือนนั้น
    //
    // ส่งเฉพาะคีย์ที่มีค่าจริง: คีย์ว่างจะกลายเป็น `?year=` ใน query string ซึ่ง BE
    // อ่านเป็น 0 (= ไม่กรอง) อยู่แล้ว แต่ทำให้ URL/log อ่านยากโดยไม่จำเป็น.
    // "เดือนโดยไม่มีปี" ถูกตัดทิ้งที่นี่ด้วย เพราะไม่มีความหมาย — BE ก็ไม่สนใจ
    // เช่นกัน แต่ตัดที่นี่ทำให้ request ตรงกับสิ่งที่ผู้ใช้เลือกจริง
    async loadBatches({ commit, state }, payload) {
      const p = payload || {};
      const params = {};
      const batchNumber = String(p.batchNumber || "").trim();
      if (batchNumber) params.batchNumber = batchNumber;
      const year = Number(p.year) || 0;
      const month = Number(p.month) || 0;
      if (year > 0) {
        params.year = year;
        if (month >= 1 && month <= 12) params.month = month;
      }
      state.isLoading = true;
      try {
        const res = await LmgService.DmsBatchList(params);
        commit("SET_BATCHES", res.data.Data == null ? [] : res.data.Data);
      } catch (err) {
        commit("SET_BATCHES", []);
      } finally {
        state.isLoading = false;
      }
    },
    // detail + items ของชุดนำส่ง — คืน object ให้หน้าใช้
    loadDetail(_, id) {
      return LmgService.DmsBatchDetail(id);
    },
    // ลบ (soft) → BE reverse ledger เอง
    deleteBatch(_, id) {
      return LmgService.DmsBatchDelete(id);
    },
    // นำส่ง (draft→submitted) → BE ตัดยอด ledger (atomic)
    submitBatch(_, id) {
      return LmgService.DmsBatchSubmit(id);
    },
    // export ใบนำส่งเป็น PDF (เอกสารนำส่งให้บริษัทประกัน) — คืน blob response
    exportPdf(_, id) {
      return LmgService.DmsBatchExportPdf(id);
    },
    // export ใบนำส่งเป็น XLSX (record ดิบ) — คืน blob response
    exportXlsx(_, id) {
      return LmgService.DmsBatchExportXlsx(id);
    },
  },
  getters: {
    getAvailable(state) {
      return state.available;
    },
    getAvailableTotal(state) {
      return state.availableTotal;
    },
    getBlocked(state) {
      return state.blocked;
    },
    getBlockedTotal(state) {
      return state.blockedTotal;
    },
    getBatches(state) {
      return state.batches;
    },
    isLoading(state) {
      return state.isLoading;
    },
    isLoadingAvailable(state) {
      return state.isLoadingAvailable;
    },
  },
};

export default DmsModule;
